// src/components/programs/JobOrders/JobOrdersList.jsx
import React, { useState } from "react";
import {
    Box,
    Tab,
    Tabs,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    IconButton,
    Tooltip
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useGetProgramJobOrders from "../../../hooks/jobOrders/useGetProgramJobOrders";
import { useLocation } from "react-router-dom";
import LoadingPage from "../../global/LoadingPage";
import { Visibility, Edit, Add, ReceiptLong } from "@mui/icons-material";
import CreateEditJobOrderModal from "./CreateEditJobOrderModal";
import PreviewJobOrderModal from "./PreviewJobOrderModal";
import PreviewJobVoucherModal from "./PreviewJobVoucherModal";

const getLocalizedText = (val, currentLang = "en") => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "number") return String(val);
    if (typeof val === "object") {
        const isAr = currentLang?.startsWith("ar");
        const primary = isAr ? "ar" : "en";
        const secondary = isAr ? "en" : "ar";
        if (val[currentLang] && typeof val[currentLang] === "string") return val[currentLang];
        if (val[primary] && typeof val[primary] === "string") return val[primary];
        if (val[secondary] && typeof val[secondary] === "string") return val[secondary];
        if (val.name) return getLocalizedText(val.name, currentLang);
        if (val.title) return getLocalizedText(val.title, currentLang);
        return "";
    }
    return String(val);
};

const JobOrdersList = ({ program }) => {
    const { i18n } = useTranslation();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isMissing = searchParams.get("missing") === "true";
    const lng = i18n.language || "en";
    const isArabic = lng?.startsWith("ar");
    const [tabIndex, setTabIndex] = useState(0);

    const { data: jobOrdersData, isLoading, refetch } = useGetProgramJobOrders(program.id);

    const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
    const [selectedOccurrence, setSelectedOccurrence] = useState(null);
    const [selectedJobOrder, setSelectedJobOrder] = useState(null);

    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewJobOrderId, setPreviewJobOrderId] = useState(null);

    const [previewVoucherModalOpen, setPreviewVoucherModalOpen] = useState(false);
    const [previewVoucherId, setPreviewVoucherId] = useState(null);

    if (isLoading) return <LoadingPage />;

    const programData = jobOrdersData?.program || jobOrdersData;
    const isProgramSettled = programData?.status === 'settled' || programData?.is_settled || program?.status === 'settled' || program?.is_settled;

    const extractOccurrences = (days) => {
        if (!days || !Array.isArray(days)) return [];
        let occurrencesList = [];
        days.forEach(day => {
            if (day.products && Array.isArray(day.products)) {
                day.products.forEach(product => {
                    const pCode = product.pricing_code || product.pricing_unit?.code || "";
                    const pType = product.productType?.code || product.product_type?.code || product.product_type || "";
                    if (
                        pCode !== "program_marker" && 
                        pCode !== "free_time" && 
                        pCode !== "free_day" &&
                        pCode !== "visa" &&
                        pType !== "visa"
                    ) {
                        occurrencesList.push({
                            ...product,
                            date: day.date,
                            day_name: day.day_name,
                            product_name: product.title,
                        });
                    }
                });
            }
        });
        return occurrencesList;
    };

    const enrichOccurrences = (occs) => {
        if (!occs || !Array.isArray(occs)) return null;
        if (occs.length === 0) return occs;
        
        return occs.map(occ => {
            let foundDay = null;
            let foundProduct = null;
            
            if (programData?.days) {
                for (const day of programData.days) {
                    if (day.id === occ.day_id || day.date === occ.date) {
                        foundDay = day;
                        if (day.products) {
                            foundProduct = day.products.find(p => 
                                p.id === occ.day_product_id || 
                                p.pivot?.id === occ.day_product_id || 
                                p.day_product_id === occ.day_product_id || 
                                p.id === occ.product_id
                            );
                        }
                        break;
                    }
                }
            }

            const displayDate = occ.date || occ.day?.date || occ.day_date || foundDay?.date || "";
            const displayProductName = getLocalizedText(occ.product_name || occ.product_snapshot?.title || occ.product?.title || foundProduct?.title, lng);
            const itineraryNote = getLocalizedText(occ.itinerary_note || foundProduct?.itinerary_note, lng);

            return {
                ...occ,
                displayDate: typeof displayDate === 'object' ? getLocalizedText(displayDate, lng) : displayDate,
                displayProductName,
                itineraryNote
            };
        });
    };

    const extractedFromDays = extractOccurrences(programData?.days).map(occ => ({
        ...occ,
        displayDate: occ.date,
        displayProductName: getLocalizedText(occ.product_name, lng),
        itineraryNote: getLocalizedText(occ.itinerary_note, lng)
    }));

    const occurrences = enrichOccurrences(jobOrdersData?.current_occurrences) || extractedFromDays || [];
    const archivedJobOrders = jobOrdersData?.archived_job_orders || programData?.archived_job_orders || [];

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const handleCreateEdit = (occurrence) => {
        setSelectedOccurrence(occurrence);
        setSelectedJobOrder(occurrence.job_order);
        setCreateEditModalOpen(true);
    };

    const handlePreview = (jobOrderId) => {
        setPreviewJobOrderId(jobOrderId);
        setPreviewModalOpen(true);
    };

    const handlePreviewVoucher = (voucherId) => {
        setPreviewVoucherId(voucherId);
        setPreviewVoucherModalOpen(true);
    };

    const renderStatusChip = (jobOrder, supplier) => {
        if (!supplier) {
            return <Chip label={isArabic ? "المورد مفقود" : "Supplier Missing"} color="error" size="small" />;
        }
        if (!jobOrder) {
            return <Chip label={isArabic ? "مفقود" : "Missing"} color="warning" size="small" />;
        }
        const statusStr = typeof jobOrder.status === 'object' ? getLocalizedText(jobOrder.status, lng) : String(jobOrder.status || "");
        if (statusStr === 'active') {
            return <Chip label={isArabic ? "نشط" : "Active"} color="success" size="small" />;
        }
        if (statusStr === 'archived') {
            return <Chip label={isArabic ? "مؤرشف" : "Archived"} color="default" size="small" />;
        }
        if (statusStr === 'cancelled') {
            return <Chip label={isArabic ? "ملغى" : "Cancelled"} color="error" size="small" />;
        }
        if (statusStr === 'pending') {
            return <Chip label={isArabic ? "قيد الانتظار" : "Pending"} color="warning" size="small" />;
        }
        return <Chip label={statusStr} size="small" />;
    };

    const getVoucherId = (jobOrder) => {
        if (!jobOrder) return null;
        if (jobOrder.job_voucher && typeof jobOrder.job_voucher === 'object') {
            return jobOrder.job_voucher.id;
        }
        if (jobOrder.job_voucher && typeof jobOrder.job_voucher === 'number') {
            return jobOrder.job_voucher;
        }
        if (jobOrder.voucher_id) {
            return jobOrder.voucher_id;
        }
        return null;
    };

    return (
        <Box>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs value={tabIndex} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
                    <Tab label={isArabic ? "الخدمات الحالية" : "Current Occurrences"} />
                    <Tab label={isArabic ? "أوامر التشغيل والفاوتشر المؤرشفة" : "Archived Job Orders & Vouchers"} />
                </Tabs>
            </Box>

            {tabIndex === 0 && (
                <TableContainer component={Paper} elevation={0} variant="outlined">
                    <Table>
                        <TableHead sx={{ backgroundColor: "grey.100" }}>
                            <TableRow>
                                <TableCell>{isArabic ? "التاريخ" : "Date"}</TableCell>
                                <TableCell>{isArabic ? "الخدمة" : "Service"}</TableCell>
                                <TableCell>{isArabic ? "المورد" : "Supplier"}</TableCell>
                                <TableCell>{isArabic ? "الحالة" : "Status"}</TableCell>
                                <TableCell>{isArabic ? "رقم الجوب اوردر" : "Job Number"}</TableCell>
                                <TableCell>{isArabic ? "تفاصيل التشغيل (الموقع/التحصيل)" : "Operational Info"}</TableCell>
                                <TableCell>{isArabic ? "الملاحظات" : "Notes & Remarks"}</TableCell>
                                <TableCell align="center">{isArabic ? "الإجراءات" : "Actions"}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {occurrences.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="textSecondary">
                                            {isArabic ? "لا توجد بيانات" : "No data available"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                occurrences.map((occ) => {
                                    const isRowMissing = isMissing && occ.supplier && !occ.job_order;
                                    const jo = occ.job_order;
                                    const joNotes = getLocalizedText(jo?.notes, lng);
                                    const joPickup = getLocalizedText(jo?.pickup_location, lng);
                                    const joDropoff = getLocalizedText(jo?.drop_off_location, lng);
                                    const joCash = typeof jo?.cash_on_tour === 'object' 
                                        ? getLocalizedText(jo.cash_on_tour, lng) 
                                        : (jo?.cash_on_tour !== undefined && jo?.cash_on_tour !== null ? String(jo.cash_on_tour) : "");
                                    const joRemarks = getLocalizedText(jo?.remarks, lng);
                                    const supplierName = getLocalizedText(occ.supplier?.name, lng) || "-";
                                    const voucherId = getVoucherId(jo);

                                    return (
                                        <TableRow 
                                            key={occ.occurrence_key}
                                            sx={isRowMissing ? { backgroundColor: "#fff4e5", border: "1px solid #ff9800" } : {}}
                                        >
                                            <TableCell>{occ.displayDate}</TableCell>
                                            <TableCell>{occ.displayProductName}</TableCell>
                                            <TableCell>{supplierName}</TableCell>
                                            <TableCell>{renderStatusChip(occ.job_order, occ.supplier)}</TableCell>
                                            <TableCell>{occ.job_order?.job_number || "-"}</TableCell>
                                            
                                            {/* Operational Info (Pickup, Drop-off, Cash on Tour) */}
                                            <TableCell>
                                                <Box sx={{ fontSize: "0.85rem", display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                    {joPickup && (
                                                        <Box>
                                                            <Typography component="span" variant="caption" fontWeight="bold" color="text.secondary">
                                                                {isArabic ? "الاستقبال: " : "Pickup: "}
                                                            </Typography>
                                                            {joPickup}
                                                        </Box>
                                                    )}
                                                    {joDropoff && (
                                                        <Box>
                                                            <Typography component="span" variant="caption" fontWeight="bold" color="text.secondary">
                                                                {isArabic ? "التوصيل: " : "Drop-off: "}
                                                            </Typography>
                                                            {joDropoff}
                                                        </Box>
                                                    )}
                                                    {joCash !== "" && (
                                                        <Box>
                                                            <Typography component="span" variant="caption" fontWeight="bold" color="error.main">
                                                                {isArabic ? "تحصيل نقدي: " : "Cash: "}
                                                            </Typography>
                                                            <Typography component="span" variant="caption" fontWeight="bold" color="error.dark">
                                                                {joCash}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {!joPickup && !joDropoff && joCash === "" && "-"}
                                                </Box>
                                            </TableCell>

                                            {/* Notes and Remarks: Job Order Notes FIRST, then others */}
                                            <TableCell sx={{ maxWidth: 240 }}>
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                    {joNotes && (
                                                        <Box sx={{
                                                            p: 0.5,
                                                            borderRadius: 1,
                                                            bgcolor: "primary.50",
                                                            border: "1px dashed",
                                                            borderColor: "primary.200"
                                                        }}>
                                                            <Typography variant="caption" color="primary.main" fontWeight="bold" display="block">
                                                                {isArabic ? "ملاحظات أمر التشغيل:" : "Job Order Notes:"}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ whiteSpace: "pre-line", fontSize: "0.85rem", color: "primary.dark" }}>
                                                                {joNotes}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {joRemarks && (
                                                        <Typography variant="caption" color="error.main" sx={{ display: "block" }}>
                                                            <strong>{isArabic ? "تنبيه: " : "Remarks: "}</strong>
                                                            {joRemarks}
                                                        </Typography>
                                                    )}
                                                    {!joNotes && !joRemarks && "-"}
                                                </Box>
                                            </TableCell>

                                            {/* Actions Column (1 button for Job Order, 1 button for Voucher, 1 button for Edit) */}
                                            <TableCell align="center">
                                                {!occ.supplier ? (
                                                    <Tooltip title={isArabic ? "يجب اختيار مورد في البرنامج أولاً" : "You must select a supplier in the program first"}>
                                                        <span>
                                                            <Button variant="contained" size="small" disabled startIcon={<Add />}>
                                                                {isArabic ? "إنشاء" : "Create"}
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                ) : !occ.job_order ? (
                                                    isProgramSettled ? (
                                                        <Chip 
                                                            label={isArabic ? "البرنامج مغلق" : "Program Settled"} 
                                                            size="small" 
                                                            color="default" 
                                                        />
                                                    ) : (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<Add />}
                                                            onClick={() => handleCreateEdit(occ)}
                                                        >
                                                            {isArabic ? "إنشاء" : "Create"}
                                                        </Button>
                                                    )
                                                ) : (
                                                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                                                        {/* 1. Job Order (Opens Preview & Print Modal) */}
                                                        <Tooltip title={isArabic ? "أمر التشغيل (معاينة وطباعة)" : "Job Order (Preview & Print)"}>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handlePreview(occ.job_order.id)}
                                                            >
                                                                <Visibility fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        {/* 2. Voucher (Opens Preview & Print Modal) */}
                                                        {voucherId && (
                                                            <Tooltip title={isArabic ? "الفاوتشر (معاينة وطباعة)" : "Voucher (Preview & Print)"}>
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{ color: "success.main" }}
                                                                    onClick={() => handlePreviewVoucher(voucherId)}
                                                                >
                                                                    <ReceiptLong fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}

                                                        {/* 3. Edit (only if active and program is not settled) */}
                                                        {!isProgramSettled && occ.job_order.status === 'active' && (
                                                            <Tooltip title={isArabic ? "تعديل" : "Edit"}>
                                                                <IconButton
                                                                    size="small"
                                                                    color="secondary"
                                                                    onClick={() => handleCreateEdit(occ)}
                                                                >
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {tabIndex === 1 && (
                <TableContainer component={Paper} elevation={0} variant="outlined">
                    <Table>
                        <TableHead sx={{ backgroundColor: "grey.100" }}>
                            <TableRow>
                                <TableCell>{isArabic ? "رقم الجوب اوردر" : "Job Number"}</TableCell>
                                <TableCell>{isArabic ? "الخدمة" : "Service"}</TableCell>
                                <TableCell>{isArabic ? "تاريخ الأرشفة" : "Archived At"}</TableCell>
                                <TableCell align="center">{isArabic ? "الإجراءات" : "Actions"}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {archivedJobOrders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography variant="body2" color="textSecondary">
                                            {isArabic ? "لا توجد بيانات" : "No data available"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                archivedJobOrders.map((jo) => {
                                    const voucherId = getVoucherId(jo);
                                    return (
                                        <TableRow key={jo.id}>
                                            <TableCell>{jo.job_number || "-"}</TableCell>
                                            <TableCell>{getLocalizedText(jo.product_snapshot?.title, lng) || "-"}</TableCell>
                                            <TableCell>{jo.archived_at ? new Date(jo.archived_at).toLocaleString() : "-"}</TableCell>
                                            <TableCell align="center">
                                                <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                                                    {/* Job Order (Opens Preview & Print Modal) */}
                                                    <Tooltip title={isArabic ? "أمر التشغيل (معاينة وطباعة)" : "Job Order (Preview & Print)"}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handlePreview(jo.id)}
                                                        >
                                                            <Visibility fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {/* Voucher (Opens Preview & Print Modal) */}
                                                    {voucherId && (
                                                        <Tooltip title={isArabic ? "الفاوتشر (معاينة وطباعة)" : "Voucher (Preview & Print)"}>
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: "success.main" }}
                                                                onClick={() => handlePreviewVoucher(voucherId)}
                                                            >
                                                                <ReceiptLong fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Modals */}
            <CreateEditJobOrderModal
                open={createEditModalOpen}
                onClose={() => setCreateEditModalOpen(false)}
                program={program}
                occurrence={selectedOccurrence}
                jobOrder={selectedJobOrder}
                onSuccess={() => refetch()}
            />

            <PreviewJobOrderModal
                open={previewModalOpen}
                onClose={() => setPreviewModalOpen(false)}
                jobOrderId={previewJobOrderId}
                program={programData}
                onPrintSuccess={() => {
                    refetch();
                }}
            />

            <PreviewJobVoucherModal
                open={previewVoucherModalOpen}
                onClose={() => setPreviewVoucherModalOpen(false)}
                jobVoucherId={previewVoucherId}
                program={programData}
                onPrintSuccess={() => {
                    refetch();
                }}
            />
        </Box>
    );
};

export default JobOrdersList;
